import { useCallback, useMemo } from 'react';
import useIsTableUsageSupported from 'shared/hooks/useIsTableUsageSupported';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import {
  AvatarGroup,
  ChipCounterList,
  FlexCenter,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import {
  Column,
  ColumnFilterOption,
  ColumnType,
} from '@elementary/common-ui/components/Table';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import {
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
} from '@elementary/common-ui/constants/urlParams';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { capitalize, distinctArray } from '@elementary/common-ui/utils/common';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import dateFilterValueMatcher from '@elementary/common-ui/utils/comparers/dateFilterValueMatcher';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import TestResultsTableCell from 'shared/cellRenderers/TestResultsTableCell';
import { getTestResultsGroup } from 'shared/utils/testExecutions';
import dateRenderer from '@elementary/common-ui/components/cellRenderers/dateRenderer';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { getFilterOptionsOverride } from '@elementary/common-ui/utils/graphqlFilters';
import TableUsagePopularity from 'shared/components/TableUsage/TableUsagePopularity';
import LastModelRunState from '../../LastModelRunState';
import { CatalogTableAssetData } from '../types';

const StyledFlexCenter = styled(FlexCenter)`
  gap: 8px;
  justify-content: flex-start;
`;

const StyledOverflowTooltip = styled(OverflowTooltip)`
  width: fit-content;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

type Props = {
  data: CatalogTableAssetData[];
  filterOptions?: Record<string, ColumnFilterOption[]> | null;
};

export const useCatalogTableColumns = ({ data, filterOptions }: Props) => {
  const { formatUrlParam } = useSelectedTreeItem();
  const navigate = useNavigateWithEnv();

  const { treeType, treeViewBy } = useTreeViewParams();
  const hasPermission = useEnvPermission();

  const isTableUsageSupported = useIsTableUsageSupported();

  const onLastRunsAtClick = useCallback(
    (id: string) => {
      const newParams = new URLSearchParams();
      const urlParam = formatUrlParam(id);

      if (urlParam) {
        newParams.set(TREE_NODE, urlParam);

        navigate(`/report/model-runs?${newParams?.toString()}`);
      }
    },
    [formatUrlParam, navigate],
  );

  const getFilterOptions = useCallback(
    (columnId: string, fallback?: ColumnFilterOption[]) =>
      getFilterOptionsOverride(columnId, filterOptions, fallback),
    [filterOptions],
  );

  return useMemo<Array<Column<CatalogTableAssetData>>>(
    () => [
      {
        id: 'usage',
        header: 'Usage',
        valueKey: 'tableUsage',
        showValueWhenEmpty: true,
        hideColumn: !isTableUsageSupported,
        filterable: false,
        width: 90,
        renderer: (row) =>
          row.tableUsage === undefined ? (
            <Typography sx={{ fontSize: '14px', color: 'common.gray4' }}>
              N/A
            </Typography>
          ) : (
            <TableUsagePopularity
              usagePercentile={
                row.tableUsage ? row.tableUsage.usagePercentile : null
              }
              periodStart={row.tableUsage?.periodStart}
              periodEnd={row.tableUsage?.periodEnd}
              queriesCount={row.tableUsage?.queriesCount}
              uniqueUserCount={row.tableUsage?.uniqueUserCount}
              showLabel={false}
            />
          ),
      },
      {
        id: 'dbName',
        header: 'Database',
        width: 160,
        filterOperators: ['equals', 'notEquals'],
        filterOptionsOverride: getFilterOptions('dbName'),
        defaultUntoggled: true,
      },
      {
        id: 'schemaName',
        header: 'Schema',
        width: 160,
        filterOperators: ['equals', 'notEquals'],
        filterOptionsOverride: getFilterOptions('schemaName'),
        defaultUntoggled: true,
      },
      {
        id: 'name',
        header: 'Name',
        isFilterCounterHidden: true,
        filterDropdownWidth: 350,
        filterOptionsOverride: getFilterOptions('name'),
        renderer: ({ name, isCritical, id }) => (
          <StyledFlexCenter>
            <StyledOverflowTooltip tooltipContent={name}>
              <StyledLink
                onClick={(e) => {
                  const newParams = new URLSearchParams();
                  const urlParam = formatUrlParam(id);

                  if (urlParam) {
                    if (treeType) {
                      newParams.set(TREE_TYPE, treeType);
                    }
                    if (treeViewBy) {
                      newParams.set(TREE_VIEW_BY, treeViewBy);
                    }
                    newParams.set(TREE_NODE, urlParam);
                    navigate(`/catalog?${newParams?.toString()}`);
                    e.stopPropagation();
                  }
                }}
              >
                {name}
              </StyledLink>
            </StyledOverflowTooltip>

            {isCritical && (
              <Tooltip title="Critical Asset" placement="top">
                <FlexCenter aria-label="critical-asset-icon">
                  <CriticalAssetIcon width={20} />
                </FlexCenter>
              </Tooltip>
            )}
          </StyledFlexCenter>
        ),
      },
      {
        id: 'assetType',
        header: 'Type',
        width: 116,
        filterOperators: ['equals', 'notEquals'],
        filterOptionsOverride: getFilterOptions('assetType'),
        renderer: ({ assetType }) => (
          <OverflowTooltip>{capitalize(assetType)}</OverflowTooltip>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        filterOperators: ['contains', 'notContains'],
        filterOptionsOverride: getFilterOptions('description'),
        hideColumnFilter: true,
      },
      {
        id: 'owners',
        header: 'Owners',
        width: 160,
        filterOptionsOverride: getFilterOptions(
          'owners',
          distinctArray(data?.flatMap((i) => i.owners))
            .map((t) => ({ label: t, value: t }))
            .concat({ label: '', value: '' }),
        ),
        renderer: ({ owners }) => <AvatarGroup items={owners} />,
      },
      {
        id: 'tags',
        header: 'Tags',
        showSelectAll: false,
        renderer: ({ tags }) => <ChipCounterList items={tags} />,
        filterOptionsOverride: getFilterOptions(
          'tags',
          distinctArray(data?.flatMap((i) => i.tags))
            .map((t) => ({ label: t, value: t }))
            .concat({ label: '', value: '' }),
        ),
      },
      {
        id: 'path',
        header: 'Path',
        defaultUntoggled: true,
        filterOptionsOverride: getFilterOptions('path'),
      },
      {
        id: 'lastRunAt',
        header: 'Last run at',
        valueKey: 'id',
        type: ColumnType.Date,
        emptyValueLabel: 'No run time',
        compareFunc: (a, b) =>
          dateComparer(
            a.latestExecution?.executionTimestamp,
            b.latestExecution?.executionTimestamp,
          ),
        filterValueMatcher: ([from, to, emptyFlag], rowData) => {
          const time = rowData.latestExecution?.executionTimestamp ?? undefined;

          return dateFilterValueMatcher(from, to, emptyFlag, time);
        },
        renderer: (rowData) => {
          const latestExecution = rowData.latestExecution;
          const status = latestExecution?.status;
          const time = latestExecution?.executionTimestamp ?? undefined;

          return (
            <Box
              onClick={(e) => {
                if (time) onLastRunsAtClick(rowData.id);
                e.stopPropagation();
              }}
              sx={{ cursor: time ? 'pointer' : 'default' }}
            >
              <LastModelRunState status={status} time={time} showOnlyTime />
            </Box>
          );
        },
      },
      {
        id: 'lastRunStatus',
        valueKey: 'id',
        header: 'Last run status',
        width: 168,
        filterable: true,
        filterOperators: ['equals', 'notEquals'],
        filterOptionsOverride: getFilterOptions('lastRunStatus', [
          { value: 'success', label: 'Success' },
          { value: 'skipped', label: 'Skipped' },
          { value: 'error', label: 'Error' },
          { value: '', label: '' },
        ]),
        filterValueMatcher: (selectedFilterValue, rowData) => {
          const latestExecution = rowData.latestExecution;
          const status = latestExecution?.status;

          return selectedFilterValue.some((val) =>
            val ? status === val : !status,
          );
        },
        compareFunc: (a, b) => {
          const statusA = a.latestExecution?.status;
          const statusB = b.latestExecution?.status;

          if (statusA && !statusB) return 1;
          if (statusB && !statusA) return -1;
          if (statusA && statusB) return statusA.localeCompare(statusB);
          return 0;
        },
        renderer: (rowData) => {
          const status = rowData.latestExecution?.status;

          return (
            <Box
              onClick={(e) => {
                if (status) onLastRunsAtClick(rowData.id);
                e.stopPropagation();
              }}
              sx={{ cursor: status ? 'pointer' : 'default' }}
            >
              <LastModelRunState status={status} showOnlyStatus />
            </Box>
          );
        },
      },
      {
        id: 'testResults',
        valueKey: 'id',
        header: 'Test results',
        width: 160,
        filterable: false,
        sortable: false,
        renderer: ({ id, latestTestExecutions }) => (
          <TestResultsTableCell
            assetId={id}
            statuses={getTestResultsGroup(latestTestExecutions)}
          />
        ),
        hideColumn: !hasPermission('VIEW_TESTS'),
      },
      {
        id: 'lastDataModificationTime',
        header: 'Last table update',
        type: ColumnType.Date,
        width: 200,
        emptyValueLabel: 'No update time',
        renderer: (val) =>
          dateRenderer(val.lastDataModificationTime ?? undefined, 'L LT'),
        compareFunc: (a, b) =>
          dateComparer(
            a.lastDataModificationTime ?? undefined,
            b.lastDataModificationTime ?? undefined,
          ),
        filterValueMatcher: ([from, to, emptyFlag], rowData) =>
          dateFilterValueMatcher(
            from,
            to,
            emptyFlag,
            rowData.lastDataModificationTime ?? undefined,
          ),
      },
      {
        id: 'materialization',
        header: 'Materialization',
        filterable: true,
        width: 180,
        filterOptionsOverride: getFilterOptions('materialization'),
        renderer: ({ materialization }) => (
          <OverflowTooltip>
            {materialization
              ? materialization[0].toUpperCase() + materialization.slice(1)
              : '-'}
          </OverflowTooltip>
        ),
      },
      {
        id: 'isCritical',
        header: 'Critical asset',
        defaultUntoggled: true,
        centerContent: true,
        filterOperators: ['equals', 'notEquals'],
        filterOptionsOverride: getFilterOptions('isCritical', [
          { value: 'true', label: 'Critical Asset' },
          { value: 'false', label: 'Not Critical' },
        ]),
        renderer: ({ isCritical }) => (
          <Typography>{isCritical ? 'True' : 'False'}</Typography>
        ),
      },
      {
        id: 'artifactType',
        header: 'Artifact type',
        filterable: true,
        defaultUntoggled: true,
        filterOperators: ['equals', 'notEquals'],
        filterOptionsOverride: getFilterOptions('artifactType'),
      },
    ],
    [
      isTableUsageSupported,
      data,
      hasPermission,
      formatUrlParam,
      treeType,
      treeViewBy,
      navigate,
      getFilterOptions,
      onLastRunsAtClick,
    ],
  );
};
